import React from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import { Chip, makeStyles, CircularProgress } from '@material-ui/core';
import { Dependency } from '../utils/types';

const useStyles = makeStyles(theme => ({
  outdated: {
    backgroundColor: theme.palette.type === 'dark' ? '#e65100' : '#fff3e0',
    color: theme.palette.type === 'dark' ? '#fff' : '#e65100',
  },
  upToDate: {
    backgroundColor: theme.palette.type === 'dark' ? '#2e7d32' : '#e8f5e9',
    color: theme.palette.type === 'dark' ? '#fff' : '#2e7d32',
  },
  unavailable: {
    backgroundColor: theme.palette.type === 'dark' ? '#616161' : '#eeeeee',
    color: theme.palette.type === 'dark' ? '#fff' : '#616161',
  },
  pending: {
    backgroundColor: theme.palette.type === 'dark' ? '#37474f' : '#eceff1',
    color: theme.palette.type === 'dark' ? '#b0bec5' : '#607d8b',
  },
  riskLow: {
    backgroundColor: theme.palette.type === 'dark' ? '#1b5e20' : '#e8f5e9',
    color: theme.palette.type === 'dark' ? '#fff' : '#1b5e20',
  },
  riskMedium: {
    backgroundColor: theme.palette.type === 'dark' ? '#e65100' : '#fff3e0',
    color: theme.palette.type === 'dark' ? '#fff' : '#e65100',
  },
  riskHigh: {
    backgroundColor: theme.palette.type === 'dark' ? '#b71c1c' : '#ffebee',
    color: theme.palette.type === 'dark' ? '#fff' : '#b71c1c',
  },
  riskNone: {
    backgroundColor: theme.palette.type === 'dark' ? '#616161' : '#eeeeee',
    color: theme.palette.type === 'dark' ? '#fff' : '#616161',
  },
}));

interface DependencyTableProps {
  dependencies: Dependency[];
  showSource?: boolean;
  /** When true, deps with latestVersion===null show "Verificando..." instead of "Indisponível" */
  resolving?: boolean;
}

const statusLabel: Record<string, string> = {
  'up-to-date': 'Atualizada',
  outdated: 'Desatualizada',
  unavailable: 'Indisponível',
};

const riskLabel: Record<string, string> = {
  none: '—',
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

export const DependencyTable = React.memo(({ dependencies, showSource, resolving }: DependencyTableProps) => {
  const classes = useStyles();

  const statusClass = (row: Dependency) => {
    // While resolving, deps that haven't been checked yet show as pending
    if (resolving && row.latestVersion === null && row.status === 'unavailable') {
      return classes.pending;
    }
    if (row.status === 'outdated') return classes.outdated;
    if (row.status === 'up-to-date') return classes.upToDate;
    return classes.unavailable;
  };

  const riskClass = (risk: string) => {
    if (risk === 'high') return classes.riskHigh;
    if (risk === 'medium') return classes.riskMedium;
    if (risk === 'low') return classes.riskLow;
    return classes.riskNone;
  };

  const columns: TableColumn<Dependency>[] = [
    {
      title: 'Pacote',
      field: 'name',
      highlight: true,
    },
    ...(showSource
      ? [
          {
            title: 'Origem',
            field: 'source' as const,
          },
        ]
      : []),
    {
      title: 'Versão Atual',
      field: 'currentVersion',
    },
    {
      title: 'Última Versão',
      field: 'latestVersion',
      render: (row: Dependency) => {
        if (row.latestVersion) return row.latestVersion;
        if (resolving && row.status === 'unavailable') {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: 0.6 }}>
              <CircularProgress size={12} />
              verificando…
            </span>
          );
        }
        return 'indisponível';
      },
    },
    {
      title: 'Status',
      field: 'status',
      render: (row: Dependency) => {
        if (resolving && row.latestVersion === null && row.status === 'unavailable') {
          return (
            <Chip
              label="Verificando…"
              size="small"
              className={classes.pending}
            />
          );
        }
        return (
          <Chip
            label={statusLabel[row.status] ?? row.status}
            size="small"
            className={statusClass(row)}
          />
        );
      },
    },
    {
      title: 'Risco',
      field: 'risk',
      render: (row: Dependency) => {
        if (resolving && row.latestVersion === null && row.status === 'unavailable') {
          return '—';
        }
        return row.risk !== 'none' ? (
          <Chip
            label={riskLabel[row.risk] ?? row.risk}
            size="small"
            className={riskClass(row.risk)}
          />
        ) : (
          '—'
        );
      },
    },
  ];

  const prodDeps = dependencies.filter(d => d.type === 'production');
  const devDeps = dependencies.filter(d => d.type === 'development');

  return (
    <>
      {prodDeps.length > 0 && (
        <Table<Dependency>
          title={`Dependências de Produção (${prodDeps.length})`}
          columns={columns}
          data={prodDeps}
          options={{
            search: true,
            paging: prodDeps.length > 20,
            pageSize: 20,
            padding: 'dense',
            sorting: true,
            emptyRowsWhenPaging: false,
          }}
        />
      )}
      {devDeps.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Table<Dependency>
            title={`Dependências de Desenvolvimento (${devDeps.length})`}
            columns={columns}
            data={devDeps}
            options={{
              search: true,
              paging: devDeps.length > 20,
              pageSize: 20,
              padding: 'dense',
              sorting: true,
              emptyRowsWhenPaging: false,
            }}
          />
        </div>
      )}
    </>
  );
});
